import { useCallback, useMemo } from "react";
import { useOutletContext } from "react-router";

import Button from "ds/components/Button";
import Tooltip from "ds/components/Tooltip";
import { TemplateDeployment } from "types/generated";
import { canRollbackDeployment } from "shared/Templates/access/canRollbackDeployment";
import { NO_ACCESS_MESSAGE } from "utils/access";

import { ChangeHistoryEntry, InputSnapshot } from "../types";
import { showRollbackConfirmationModal } from "../RollbackConfirmationModal";
import { diffInputs } from "../helpers";

type TemplateDeploymentRollbackButtonProps = {
  entry: ChangeHistoryEntry;
  templateId: string;
  deploymentId: string;
  currentInputs: InputSnapshot[];
  currentVersionNumber: string;
};

const TemplateDeploymentRollbackButton = ({
  entry,
  templateId,
  deploymentId,
  currentInputs,
  currentVersionNumber,
}: TemplateDeploymentRollbackButtonProps) => {
  const { deployment } = useOutletContext<{ deployment: TemplateDeployment }>();
  const rollbackAccess = canRollbackDeployment(deployment);

  const hasChanges = useMemo(
    () =>
      currentVersionNumber !== entry.targetVersionNumber ||
      diffInputs(entry.inputs, currentInputs).length > 0,
    [currentVersionNumber, entry.targetVersionNumber, entry.inputs, currentInputs]
  );

  const isDisabled = !rollbackAccess.result || !hasChanges;
  const tooltipMessage = !rollbackAccess.result
    ? (rollbackAccess.message ?? NO_ACCESS_MESSAGE)
    : "No changes to roll back to this state";

  const handleRollback = useCallback(() => {
    showRollbackConfirmationModal({
      entry,
      templateId,
      deploymentId,
      currentInputs,
      currentVersionNumber,
    });
  }, [entry, templateId, deploymentId, currentInputs, currentVersionNumber]);

  return (
    <Tooltip
      active={isDisabled}
      on={(props) => (
        <Button
          {...props}
          variant="secondary"
          size="small"
          onPress={handleRollback}
          disabled={isDisabled}
        >
          Revert to
        </Button>
      )}
    >
      {tooltipMessage}
    </Tooltip>
  );
};

export default TemplateDeploymentRollbackButton;
